import { motion } from 'framer-motion';
import { Mail, Lock, User, ShieldAlert, Key, Globe, LogIn, Smartphone, ShieldCheck, HardDrive, ChevronRight, CheckCircle, Copy, Fingerprint, Building2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const API = (import.meta as any).env.VITE_API_URL || 'https://crisis-monitoring-api-170585558576.us-central1.run.app';

// ─── Shared card wrapper ─────────────────────────────────────────────────────
function AuthCard({ children, glowColor = '#06b6d4' }: { children: React.ReactNode; glowColor?: string }) {
  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full pointer-events-none blur-[120px]"
        style={{ background: glowColor + '18' }} />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -12, scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        className="w-full max-w-md relative z-10"
      >
        {children}
      </motion.div>
    </div>
  );
}

function CrisisLogo({ onClick }: { onClick?: () => void }) {
  return (
    <div className="flex justify-center mb-7 cursor-pointer" onClick={onClick}>
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl bg-cyan-500 blur-md opacity-40" />
        <div className="relative p-3 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl shadow-xl">
          <ShieldAlert className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );
}

// ─── MFA Method Selector (step 1 of MFA setup) ───────────────────────────────
function MFAMethodSelector({
  onSelect,
  onSkip,
}: {
  onSelect: (method: 'passkey' | 'totp' | 'hardware') => void;
  onSkip: () => void;
}) {
  const [selected, setSelected] = useState<'passkey' | 'totp' | 'hardware'>('passkey');
  const [deviceName, setDeviceName] = useState('');

  const methods: Array<{
    id: 'passkey' | 'totp' | 'hardware';
    title: string;
    icon: React.FC<any>;
    color: string;
    desc: string;
    recommended?: boolean;
  }> = [
    {
      id: 'passkey' as const,
      title: 'Passkey or Security key',
      icon: Fingerprint,
      color: '#06b6d4',
      desc: 'Authenticate using your fingerprint, face, or screen lock. Uses your device\'s built-in biometric sensor or a FIDO2 hardware security key.',
      recommended: true,
    },
    {
      id: 'totp' as const,
      title: 'Authenticator app',
      icon: Smartphone,
      color: '#8b5cf6',
      desc: 'Generate a 6-digit code using an app on your phone — Google Authenticator, Authy, or Microsoft Authenticator.',
    },
    {
      id: 'hardware' as const,
      title: 'Hardware TOTP Token',
      icon: HardDrive,
      color: '#f59e0b',
      desc: 'Generate a code using a dedicated hardware TOTP token or other hardware device.',
    },
  ];

  return (
    <AuthCard glowColor="#06b6d4">
      <div className="bg-zinc-900/50 backdrop-blur-2xl border border-zinc-800/60 rounded-3xl p-7 shadow-2xl">
        <CrisisLogo />
        <div className="text-center mb-6">
          <h2 className="text-2xl font-black text-white mb-1">Keep your account secure</h2>
          <div className="flex items-center gap-2 justify-center mt-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/25">
            <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-amber-300 text-sm font-medium">Registering MFA for your account is strongly recommended</p>
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">MFA device name</label>
          <input
            value={deviceName}
            onChange={e => setDeviceName(e.target.value)}
            placeholder="e.g. My iPhone, YubiKey 5"
            className="w-full bg-zinc-950/60 border border-zinc-700 text-white rounded-xl py-2.5 px-4 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 placeholder:text-zinc-400"
          />
          <p className="text-zinc-400 text-xs mt-1">Maximum 128 characters. Alphanumeric and '+&#160;=&#160;,&#160;.&#160;@&#160;-&#160;_' allowed.</p>
        </div>

        <div className="space-y-2.5">
          {methods.map(m => {
            const Icon = m.icon;
            const active = selected === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m.id)}
                className="w-full text-left rounded-2xl border-2 p-4 transition-all"
                style={{
                  borderColor: active ? m.color : 'rgba(63,63,70,0.6)',
                  background: active ? m.color + '0d' : 'rgba(9,9,11,0.4)',
                }}
              >
                <div className="flex items-start gap-3.5">
                  {/* Radio */}
                  <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                    style={{ borderColor: active ? m.color : '#52525b' }}>
                    {active && <div className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />}
                  </div>
                  {/* Icon */}
                  <div className="flex-shrink-0 p-2 rounded-xl" style={{ background: m.color + '18' }}>
                    <Icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  {/* Text */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-[15px]">{m.title}</span>
                      {m.recommended && (
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/25">RECOMMENDED</span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{m.desc}</p>
                    {m.recommended && (
                      <p className="text-zinc-500 text-xs mt-1 italic">CrisisShield recommends passkeys as they provide the strongest protection against phishing.</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onSkip}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-zinc-300 bg-zinc-800/60 border border-zinc-700/50 hover:bg-zinc-800 transition-colors"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => onSelect(selected)}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all"
            style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', boxShadow: '0 0 20px rgba(6,182,212,0.3)' }}
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </AuthCard>
  );
}

// ─── TOTP Setup ───────────────────────────────────────────────────────────────
function MFATOTPSetup({
  accessToken,
  onComplete,
  onBack,
}: {
  accessToken: string;
  onComplete: () => void;
  onBack: () => void;
}) {
  const [_stage, _setStage] = useState<'qr' | 'verify'>('qr');
  const [qrData, setQrData] = useState<{ secret: string; qr_code_base64: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mfa/setup/totp/begin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to start TOTP setup');
      const data = await res.json();
      setQrData(data);
      _setStage('qr');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async () => {
    if (!code || code.length !== 6) { setError('Enter a 6-digit code'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/mfa/setup/totp/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ code, device_name: 'Authenticator App' }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        throw new Error(d?.detail || 'Invalid code. Try again.');
      }
      onComplete();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (qrData) { navigator.clipboard.writeText(qrData.secret); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  if (!qrData && !loading) {
    startSetup();
    return (
      <AuthCard glowColor="#8b5cf6">
        <div className="bg-zinc-900/50 border border-zinc-800/60 rounded-3xl p-8 text-center">
          <div className="w-10 h-10 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin mx-auto" />
          <p className="text-zinc-400 mt-4 text-sm">Generating your TOTP secret…</p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard glowColor="#8b5cf6">
      <div className="bg-zinc-900/50 backdrop-blur-2xl border border-zinc-800/60 rounded-3xl p-7 shadow-2xl">
        <CrisisLogo />
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/25 mb-3">
            <Smartphone className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-purple-400 text-xs font-bold tracking-wide">AUTHENTICATOR APP</span>
          </div>
          <h2 className="text-xl font-black text-white">Set up authenticator app</h2>
          <p className="text-zinc-400 text-sm mt-1">Scan the QR code with your app, then enter the 6-digit code</p>
        </div>

        {loading && !qrData ? (
          <div className="h-48 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
          </div>
        ) : qrData && (
          <>
            {/* QR code */}
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-white rounded-2xl shadow-xl">
                <img src={qrData.qr_code_base64} alt="TOTP QR Code" className="w-40 h-40" />
              </div>
            </div>

            {/* Manual entry */}
            <div className="mb-5">
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 text-center">Or enter manually</p>
              <div className="flex items-center gap-2 bg-zinc-950/60 border border-zinc-700/60 rounded-xl px-3 py-2.5">
                <code className="flex-1 text-cyan-400 text-xs font-mono break-all">{qrData.secret}</code>
                <button type="button" onClick={copySecret} className="flex-shrink-0 p-1.5 rounded-lg hover:bg-zinc-800 transition-colors">
                  {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-zinc-500" />}
                </button>
              </div>
            </div>

            {/* 6-digit code input */}
            <div className="mb-4">
              <label className="block text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 text-center">Enter the 6-digit code from your app</label>
              <div className="flex justify-center gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <input
                    key={i}
                    ref={el => { inputRefs.current[i] = el; }}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                    value={code[i] || ''}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/, '');
                      const next = code.split('');
                      next[i] = v;
                      const newCode = next.join('').slice(0, 6);
                      setCode(newCode);
                      if (v && i < 5) inputRefs.current[i + 1]?.focus();
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Backspace' && !code[i] && i > 0) inputRefs.current[i - 1]?.focus();
                    }}
                    className="w-10 h-12 text-center text-xl font-black text-white bg-zinc-900 border-2 rounded-xl focus:outline-none transition-all"
                    style={{ borderColor: code[i] ? '#8b5cf6' : 'rgba(63,63,70,0.8)' }}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-3 px-3 py-2.5 bg-rose-500/10 border border-rose-500/25 rounded-xl">
                <p className="text-rose-400 text-sm text-center">{error}</p>
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 mt-2">
          <button type="button" onClick={onBack}
            className="px-4 py-3 rounded-xl font-bold text-sm text-zinc-400 bg-zinc-800/60 border border-zinc-700/50 hover:bg-zinc-800 transition-colors">
            ← Back
          </button>
          <button type="button" onClick={confirmCode} disabled={loading || code.length < 6}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', boxShadow: '0 0 20px rgba(139,92,246,0.3)' }}>
            {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Verify &amp; Activate</>}
          </button>
        </div>
      </div>
    </AuthCard>
  );
}

// ─── WebAuthn encoding helpers ────────────────────────────────────────────────
// WebAuthn credential IDs and challenges are base64url-encoded (RFC 4648 §5),
// which uses '-' and '_' instead of '+' and '/' and omits padding. The plain
// browser atob() only understands standard base64, so decoding a base64url
// credential id with atob() yields the wrong bytes (or throws). That makes the
// allowCredentials id mismatch the real passkey on the device, and the
// authenticator fails with "operation either timed out or was not allowed"
// (NotAllowedError). This helper accepts BOTH base64 and base64url safely.
function b64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Encode an ArrayBuffer to base64url (no padding) — the format the server's
// py_webauthn verification expects for rawId, attestationObject, clientDataJSON,
// authenticatorData and signature. A simple loop avoids stack overflow on large
// buffers that String.fromCharCode(...spread) can cause.
function bytesToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── Passkey Setup ────────────────────────────────────────────────────────────
function MFAPasskeySetup({
  accessToken,
  onComplete,
  onBack,
}: {
  accessToken: string;
  onComplete: () => void;
  onBack: () => void;
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const startPasskeySetup = async () => {
    setStatus('loading');
    setError(null);
    try {
      // 1. Get registration options from server
      const res = await fetch(`${API}/auth/mfa/setup/passkey/begin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to start passkey setup');
      const { options } = await res.json();

      // 2. Convert base64url challenge to ArrayBuffer
      const challengeBytes = b64ToBytes(options.challenge);
      const userIdBytes = b64ToBytes(options.user.id);

      // 3. Call WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: {
          ...options,
          challenge: challengeBytes,
          user: { ...options.user, id: userIdBytes },
          pubKeyCredParams: options.pubKeyCredParams,
        } as PublicKeyCredentialCreationOptions,
      }) as PublicKeyCredential;

      if (!credential) throw new Error('No credential returned from device');

      const response = credential.response as AuthenticatorAttestationResponse;

      // 4. Send credential to server (base64url-encoded for py_webauthn)
      const completeRes = await fetch(`${API}/auth/mfa/setup/passkey/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: bytesToB64url(credential.rawId),
            type: credential.type,
            authenticatorAttachment: (credential as any).authenticatorAttachment ?? undefined,
            clientExtensionResults: credential.getClientExtensionResults(),
            response: {
              attestationObject: bytesToB64url(response.attestationObject),
              clientDataJSON: bytesToB64url(response.clientDataJSON),
              transports: (response as any).getTransports ? (response as any).getTransports() : undefined,
            },
          },
          device_name: 'Passkey',
        }),
      });

      if (!completeRes.ok) {
        const d = await completeRes.json().catch(() => null);
        throw new Error(d?.detail || 'Failed to register passkey');
      }
      setStatus('success');
      setTimeout(onComplete, 1500);
    } catch (e: any) {
      setError(e.message || 'Passkey setup failed');
      setStatus('error');
    }
  };

  return (
    <AuthCard glowColor="#06b6d4">
      <div className="bg-zinc-900/50 backdrop-blur-2xl border border-zinc-800/60 rounded-3xl p-7 shadow-2xl">
        <CrisisLogo />
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 mb-3">
            <Fingerprint className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-cyan-400 text-xs font-bold tracking-wide">PASSKEY / SECURITY KEY</span>
          </div>
          <h2 className="text-xl font-black text-white">Register your passkey</h2>
          <p className="text-zinc-400 text-sm mt-1">Use your fingerprint, Face ID, or a security key to authenticate instantly</p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-emerald-400 font-bold text-lg">Passkey registered!</p>
            <p className="text-zinc-500 text-sm mt-1">Redirecting…</p>
          </div>
        ) : (
          <>
            <div className="bg-zinc-800/40 border border-zinc-700/50 rounded-2xl p-5 mb-5 space-y-3">
              {[
                { icon: '🔒', text: 'Your passkey is stored securely on your device and never leaves it' },
                { icon: '🛡️', text: 'Immune to phishing — only works on the real CrisisShield site' },
                { icon: '⚡', text: 'One touch to authenticate — no codes to type' },
              ].map(item => (
                <div key={item.text} className="flex items-start gap-3">
                  <span className="text-lg leading-none">{item.icon}</span>
                  <p className="text-zinc-300 text-sm">{item.text}</p>
                </div>
              ))}
            </div>

            {error && (
              <div className="mb-4 px-3 py-2.5 bg-rose-500/10 border border-rose-500/25 rounded-xl">
                <p className="text-rose-400 text-sm text-center">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={onBack}
                className="px-4 py-3 rounded-xl font-bold text-sm text-zinc-400 bg-zinc-800/60 border border-zinc-700/50 hover:bg-zinc-800 transition-colors">
                ← Back
              </button>
              <button type="button" onClick={startPasskeySetup} disabled={status === 'loading'}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
                style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', boxShadow: '0 0 20px rgba(6,182,212,0.3)' }}>
                {status === 'loading'
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Waiting for device…</>
                  : <><Fingerprint className="w-4 h-4" /> Create passkey</>}
              </button>
            </div>
          </>
        )}
      </div>
    </AuthCard>
  );
}

// ─── MFA Verify (step 2 of login) ────────────────────────────────────────────
function MFAVerify({
  mfaType,
  mfaSessionToken,
  deviceName,
  onSuccess,
  onCancel,
}: {
  mfaType: string;
  mfaSessionToken: string;
  deviceName?: string;
  onSuccess: (tokens: any) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const verifyTOTP = async () => {
    if (code.length !== 6) { setError('Enter a 6-digit code'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/mfa/verify/totp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_session_token: mfaSessionToken, code }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        throw new Error(d?.detail || 'Invalid code');
      }
      const data = await res.json();
      onSuccess(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyPasskey = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Get challenge from server
      const beginRes = await fetch(`${API}/auth/mfa/verify/passkey/begin?mfa_session_token=${encodeURIComponent(mfaSessionToken)}`, {
        method: 'POST',
      });
      if (!beginRes.ok) throw new Error('Failed to start passkey verification');
      const { options } = await beginRes.json();

      // 2. Convert base64url challenge
      const challengeBytes = b64ToBytes(options.challenge);

      const toPublicKeyRequest = (source: any, includeAllowCredentials: boolean): PublicKeyCredentialRequestOptions => ({
        ...source,
        challenge: challengeBytes,
        allowCredentials: includeAllowCredentials
          ? source.allowCredentials?.map((c: any) => ({
              ...c,
              id: b64ToBytes(c.id),
            }))
          : undefined,
      } as PublicKeyCredentialRequestOptions);

      // 3. WebAuthn get
      // First attempt uses allowCredentials (precise credential match).
      // If that is rejected, retry once without allowCredentials to enable
      // discoverable/hybrid QR passkey selection on a phone.
      let assertion: PublicKeyCredential | null = null;
      try {
        assertion = await navigator.credentials.get({
          publicKey: toPublicKeyRequest(options, true),
        }) as PublicKeyCredential;
      } catch (firstErr: any) {
        if (firstErr?.name !== 'NotAllowedError' || !options.allowCredentials?.length) {
          throw firstErr;
        }
        assertion = await navigator.credentials.get({
          publicKey: toPublicKeyRequest(options, false),
        }) as PublicKeyCredential;
      }

      if (!assertion) throw new Error('No assertion from device');
      const resp = assertion.response as AuthenticatorAssertionResponse;

      // 4. Send to server (base64url-encoded for py_webauthn signature check)
      const completeRes = await fetch(`${API}/auth/mfa/verify/passkey/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mfa_session_token: mfaSessionToken,
          assertion: {
            id: assertion.id,
            rawId: bytesToB64url(assertion.rawId),
            type: assertion.type,
            authenticatorAttachment: (assertion as any).authenticatorAttachment ?? undefined,
            clientExtensionResults: assertion.getClientExtensionResults(),
            response: {
              authenticatorData: bytesToB64url(resp.authenticatorData),
              clientDataJSON: bytesToB64url(resp.clientDataJSON),
              signature: bytesToB64url(resp.signature),
              userHandle: resp.userHandle ? bytesToB64url(resp.userHandle) : undefined,
            },
          },
        }),
      });
      if (!completeRes.ok) {
        const d = await completeRes.json().catch(() => null);
        throw new Error(d?.detail || 'Passkey verification failed');
      }
      const data = await completeRes.json();
      onSuccess(data);
    } catch (e: any) {
      // Map common WebAuthn failures to actionable messages.
      let msg = e?.message || 'Passkey verification failed';
      if (e?.name === 'NotAllowedError') {
        msg = 'Passkey request timed out, was cancelled, or no matching passkey was found. '
          + 'Retry and keep the passkey prompt open while scanning QR and approving on your phone.';
      } else if (e?.name === 'SecurityError') {
        msg = 'Passkey blocked for this site (domain mismatch). Please use https://www.factcheckanalytics.com.';
      } else if (e?.name === 'InvalidStateError') {
        msg = 'This device has no registered passkey for your account.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isPasskey = mfaType === 'passkey';

  return (
    <AuthCard glowColor={isPasskey ? '#06b6d4' : '#8b5cf6'}>
      <div className="bg-zinc-900/50 backdrop-blur-2xl border border-zinc-800/60 rounded-3xl p-7 shadow-2xl">
        <CrisisLogo />
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
            style={{ background: isPasskey ? 'rgba(6,182,212,0.1)' : 'rgba(139,92,246,0.1)', border: `1px solid ${isPasskey ? 'rgba(6,182,212,0.25)' : 'rgba(139,92,246,0.25)'}` }}>
            {isPasskey ? <Fingerprint className="w-3.5 h-3.5 text-cyan-400" /> : <Smartphone className="w-3.5 h-3.5 text-purple-400" />}
            <span className="text-xs font-bold tracking-wide" style={{ color: isPasskey ? '#06b6d4' : '#8b5cf6' }}>
              {isPasskey ? 'PASSKEY VERIFICATION' : 'AUTHENTICATOR CODE'}
            </span>
          </div>
          <h2 className="text-xl font-black text-white">Two-factor authentication</h2>
          {deviceName && <p className="text-zinc-500 text-sm mt-1">Device: <span className="text-zinc-300">{deviceName}</span></p>}
        </div>

        {isPasskey ? (
          <>
            <div className="text-center py-4 mb-4">
              <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center mx-auto mb-4">
                <Fingerprint className="w-10 h-10 text-cyan-400" />
              </div>
              <p className="text-zinc-300 text-sm">Use your device's biometric sensor or security key to verify your identity</p>
            </div>
            {error && <div className="mb-4 px-3 py-2.5 bg-rose-500/10 border border-rose-500/25 rounded-xl text-center"><p className="text-rose-400 text-sm">{error}</p></div>}
            <button type="button" onClick={verifyPasskey} disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
              style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', boxShadow: '0 0 20px rgba(6,182,212,0.3)' }}>
              {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Waiting for device…</> : <><Fingerprint className="w-4 h-4" /> Verify with passkey</>}
            </button>
          </>
        ) : (
          <>
            <div className="mb-5">
              <label className="block text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 text-center">Enter 6-digit code</label>
              <div className="flex justify-center gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <input
                    key={i}
                    ref={el => { inputRefs.current[i] = el; }}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                    value={code[i] || ''}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/, '');
                      const next = code.split('');
                      next[i] = v;
                      const newCode = next.join('').slice(0, 6);
                      setCode(newCode);
                      if (v && i < 5) inputRefs.current[i + 1]?.focus();
                      if (newCode.length === 6) verifyTOTP();
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Backspace' && !code[i] && i > 0) inputRefs.current[i - 1]?.focus();
                    }}
                    className="w-11 h-13 text-center text-xl font-black text-white bg-zinc-900 border-2 rounded-xl focus:outline-none transition-all p-2"
                    style={{ borderColor: code[i] ? '#8b5cf6' : 'rgba(63,63,70,0.8)' }}
                  />
                ))}
              </div>
              <p className="text-zinc-400 text-xs text-center mt-3">Open your authenticator app to get a 6-digit code</p>
            </div>
            {error && <div className="mb-3 px-3 py-2.5 bg-rose-500/10 border border-rose-500/25 rounded-xl text-center"><p className="text-rose-400 text-sm">{error}</p></div>}
            <button type="button" onClick={verifyTOTP} disabled={loading || code.length < 6}
              className="w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', boxShadow: '0 0 20px rgba(139,92,246,0.3)' }}>
              {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Verify code</>}
            </button>
          </>
        )}

        <button type="button" onClick={onCancel} className="w-full mt-3 text-center text-zinc-400 hover:text-zinc-400 text-sm transition-colors">
          ← Back to login
        </button>
      </div>
    </AuthCard>
  );
}

// ─── MFA Success screen ───────────────────────────────────────────────────────
function MFASuccess({ onDone }: { onDone: () => void }) {
  return (
    <AuthCard glowColor="#22c55e">
      <div className="bg-zinc-900/50 backdrop-blur-2xl border border-zinc-800/60 rounded-3xl p-8 text-center shadow-2xl">
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-black text-white mb-2">MFA Activated!</h2>
        <p className="text-zinc-400 text-sm mb-6">Your account is now protected with multi-factor authentication. From your next login you'll be asked to verify with your MFA device.</p>
        <button type="button" onClick={onDone}
          className="w-full py-3 rounded-xl font-bold text-white transition-all"
          style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', boxShadow: '0 0 20px rgba(34,197,94,0.3)' }}>
          Continue to Dashboard
        </button>
      </div>
    </AuthCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Login Component
// ══════════════════════════════════════════════════════════════════════════════
export function Login({ setPage, onAuth, authNotice }: { setPage: (page: any) => void, onAuth: () => void, authNotice?: string | null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // MFA state
  const [mfaState, setMfaState] = useState<null | {
    mfaRequired: boolean;
    mfaType: string;
    mfaSessionToken: string;
    mfaDeviceName?: string;
  }>(null);
  const [setupState, setSetupState] = useState<null | {
    method: 'method-select' | 'totp' | 'hardware' | 'passkey';
    accessToken: string;
  }>(null);
  const [mfaSuccess, setMfaSuccess] = useState(false);

  useEffect(() => {
    if (authNotice) {
      setInfo(authNotice);
    }
  }, [authNotice]);

  const persistAuthSession = (data: any) => {
    localStorage.setItem('crisis_auth', 'true');
    localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    
    // Store user organization profile
    const userProfile = {
      username: data.user?.username || data.user?.email?.split('@')[0] || 'admin',
      email: data.user?.email || 'admin@factcheckanalytics.com',
      role: data.user?.role || 'admin',
      is_admin: data.user?.is_admin || (data.user?.role === 'admin') || false,
      organization_name: data.user?.organization_name || 'CrisisShield',
      organization_type: data.user?.organization_type || 'other', // Least-privilege fallback; server is authoritative
      profile_completed: Boolean(data.user?.profile_completed),
      requires_profile_setup: Boolean(data.user?.requires_profile_setup),
      location_input: data.user?.location_input || null,
      location_country: data.user?.location_country || null,
      location_region: data.user?.location_region || null,
      scope_type: data.user?.scope_type || 'GLOBAL',
      country_code: data.user?.scope_country_code || data.user?.country_code || null,
      region_code: data.user?.scope_region_code || data.user?.region_code || null,
    };
    localStorage.setItem('user_profile', JSON.stringify(userProfile));
  };

  const handleAuth = (data: any) => {
    persistAuthSession(data);
    onAuth();
    setPage('home');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    setLoading(true);

    fetch(`${API}/auth/token?username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, { method: 'POST' })
      .then(async res => {
        if (!res.ok) {
          const d = await res.json().catch(() => null);
          throw new Error(d?.detail || 'Authentication failed. Please check your credentials.');
        }
        return res.json();
      })
      .then(data => {
        if (data.mfa_required) {
          // Password OK but MFA needed
          setMfaState({
            mfaRequired: true,
            mfaType: data.mfa_type,
            mfaSessionToken: data.mfa_session_token,
            mfaDeviceName: data.user?.mfa_device_name,
          });
        } else if (data.access_token) {
          // No MFA set up → offer MFA setup
          const token = data.access_token;
          localStorage.setItem('__temp_token', token);
          // Persist profile and admin flags now so feature gating never uses stale local data.
          persistAuthSession(data);
          // Show MFA setup prompt (with skip option)
          setSetupState({ method: 'method-select', accessToken: token });
        }
      })
      .catch(err => {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('Network issue while logging in. Please try again.');
        } else {
          setError(err.message);
        }
      })
      .finally(() => setLoading(false));
  };

  // ── MFA Verify screen (step 2 of login when MFA already enabled) ───────────
  if (mfaState) {
    return (
      <MFAVerify
        mfaType={mfaState.mfaType}
        mfaSessionToken={mfaState.mfaSessionToken}
        deviceName={mfaState.mfaDeviceName}
        onSuccess={handleAuth}
        onCancel={() => setMfaState(null)}
      />
    );
  }

  // ── MFA Setup flow (first login / voluntary setup) ─────────────────────────
  if (setupState) {
    if (mfaSuccess) {
      return <MFASuccess onDone={() => { onAuth(); setPage('home'); }} />;
    }
    if (setupState.method === 'method-select') {
      return (
        <MFAMethodSelector
          onSelect={method => setSetupState({ ...setupState, method })}
          onSkip={() => { onAuth(); setPage('home'); }}
        />
      );
    }
    if (setupState.method === 'totp' || setupState.method === 'hardware') {
      return (
        <MFATOTPSetup
          accessToken={setupState.accessToken}
          onComplete={() => setMfaSuccess(true)}
          onBack={() => setSetupState({ ...setupState, method: 'method-select' })}
        />
      );
    }
    if (setupState.method === 'passkey') {
      return (
        <MFAPasskeySetup
          accessToken={setupState.accessToken}
          onComplete={() => setMfaSuccess(true)}
          onBack={() => setSetupState({ ...setupState, method: 'method-select' })}
        />
      );
    }
  }

  // ── Normal login form ───────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/80 backdrop-blur-md flex items-center justify-center p-6 overflow-y-auto">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />
      
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <CrisisLogo onClick={() => setPage('landing')} />
        
        <div className="bg-zinc-900/40 backdrop-blur-2xl border border-zinc-800/50 rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Secure Login</h2>
            <p className="text-zinc-400 text-sm">Access your agency dashboard</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-4 py-3 bg-rose-500/10 border border-rose-500/30 rounded-xl">
                <p className="text-rose-400 text-sm font-medium">{error}</p>
              </motion.div>
            )}
            {info && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <p className="text-amber-400 text-sm font-medium">{info}</p>
              </motion.div>
            )}
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input name="email" type="email" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="analyst@factcheckanalytics.com" />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 flex justify-between">
                <span>Password</span>
                <button type="button" className="text-cyan-500 hover:text-cyan-400 capitalize normal-case tracking-normal">Forgot?</button>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input name="password" type="password" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="••••••••" />
              </div>
            </div>
            
            <button disabled={loading} type="submit" className="w-full flex items-center justify-center gap-2 py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-bold rounded-xl transition-colors shadow-[0_0_20px_rgba(6,182,212,0.3)] disabled:opacity-50 disabled:cursor-not-allowed mt-2">
              {loading ? ( <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> ) : ( <><LogIn className="w-5 h-5" /> Authenticate</> )}
            </button>
          </form>
          
          <div className="mt-8 text-center text-sm text-zinc-500 border-t border-zinc-800/50 pt-6">
            Don't have an account? <button onClick={() => setPage('signup')} className="text-white hover:text-cyan-400 font-bold transition-colors">Apply for Access</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function Signup({ setPage }: { setPage: (page: any) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [idProof, setIdProof] = useState<{ data: string; name: string; type: string } | null>(null);

  const MAX_ID_PROOF_BYTES = 5 * 1024 * 1024; // 5 MB
  const ALLOWED_ID_PROOF_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];

  const handleIdProofChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) { setIdProof(null); return; }
    if (!ALLOWED_ID_PROOF_TYPES.includes(file.type)) {
      setError('Identity proof must be a PNG, JPG, WEBP or PDF file.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_ID_PROOF_BYTES) {
      setError('Identity proof file is too large. Maximum size is 5 MB.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setIdProof({ data: reader.result as string, name: file.name, type: file.type });
    reader.onerror = () => setError('Could not read the selected file. Please try again.');
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const formData = new FormData(e.target as HTMLFormElement);
    const agencyId = formData.get('agency') as string;
    const email = formData.get('email') as string;
    const organizationType = formData.get('organization_type') as string;

    if (!agencyId || agencyId.toLowerCase() !== 'factcheckanalytics') {
      setError('Agency ID / Organization must be exactly "factcheckanalytics".');
      return;
    }
    if (!email || !email.toLowerCase().endsWith('@factcheckanalytics.com')) {
      setError('Registration is restricted to the @factcheckanalytics.com domain.');
      return;
    }
    if (!organizationType) {
      setError('Please select an organization type.');
      return;
    }
    if (!idProof) {
      setError('Please upload an identity proof document.');
      return;
    }

    setLoading(true);
    fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: formData.get('name'),
        agency: agencyId,
        email,
        password: formData.get('password'),
        organization_name: formData.get('organization_name'),
        organization_type: organizationType,
        identity_proof: idProof.data,
        identity_proof_filename: idProof.name,
        identity_proof_type: idProof.type,
      }),
    })
      .then(async res => {
        if (!res.ok) {
          const d = await res.json().catch(() => null);
          throw new Error(d?.detail || 'Registration failed. Please try again.');
        }
        return res.json();
      })
      .then(() => { setSuccess(true); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/80 backdrop-blur-md flex items-center justify-center p-6 overflow-y-auto py-24">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 blur-[120px] rounded-full pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <CrisisLogo onClick={() => setPage('landing')} />
        <div className="bg-zinc-900/40 backdrop-blur-2xl border border-zinc-800/50 rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">{success ? 'Registration Submitted' : 'Request Access'}</h2>
            <p className="text-zinc-400 text-sm">{success ? 'Your account requires superadmin approval.' : 'Register your agency credentials'}</p>
          </div>
          {success ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-6">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 mb-6">
                <div className="text-3xl mb-3">✅</div>
                <p className="text-emerald-400 text-sm font-medium">Successfully registered!</p>
                <p className="text-zinc-300 text-sm mt-3">We've emailed you a <strong>verification link</strong> — please click it to verify your email address. After verification, a superadmin will review and approve your access.</p>
              </div>
              <button onClick={() => setPage('login')} className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-bold rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" /> Go to Sign In
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-4 py-3 bg-rose-500/10 border border-rose-500/30 rounded-xl">
                  <p className="text-rose-400 text-sm font-medium">{error}</p>
                </motion.div>
              )}
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input name="name" type="text" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="John Doe" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Agency ID / Organization</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input name="agency" type="text" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="factcheckanalytics" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Organization Name</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input name="organization_name" type="text" className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="e.g. Reuters Newsroom" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Organization Type</label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none z-10" />
                  <select name="organization_type" required defaultValue="" className="w-full appearance-none bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-10 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all">
                    <option value="" disabled>Select organization type…</option>
                    <option value="news_media">News &amp; Media</option>
                    <option value="financial_services">Financial Services</option>
                    <option value="government">Government &amp; Public Sector</option>
                    <option value="enterprise_security">Enterprise Security</option>
                    <option value="critical_infrastructure">Critical Infrastructure</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 rotate-90 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Secure Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input name="email" type="email" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="analyst@factcheckanalytics.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Create Passphrase</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input name="password" type="password" required className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all placeholder:text-zinc-400" placeholder="••••••••" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Identity Proof</label>
                <label htmlFor="identity_proof" className="flex items-center gap-3 w-full bg-zinc-950/50 border border-dashed border-zinc-700 hover:border-cyan-500 text-zinc-300 rounded-xl py-3 px-4 cursor-pointer transition-colors">
                  <HardDrive className="w-5 h-5 text-zinc-500 shrink-0" />
                  <span className="text-sm truncate">{idProof ? idProof.name : 'Upload ID / official document'}</span>
                  {idProof && <CheckCircle className="w-4 h-4 text-emerald-400 ml-auto shrink-0" />}
                </label>
                <input id="identity_proof" name="identity_proof" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={handleIdProofChange} className="hidden" />
                <p className="text-[11px] text-zinc-500 mt-1.5">PNG, JPG, WEBP or PDF · max 5 MB. Used to verify your identity for access approval.</p>
              </div>
              <button disabled={loading} type="submit" className="w-full py-3 bg-white hover:bg-zinc-200 text-zinc-950 font-bold rounded-xl transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mt-4 flex items-center justify-center">
                {loading ? <span className="w-5 h-5 border-2 border-zinc-950/30 border-t-zinc-950 rounded-full animate-spin" /> : 'Submit Registration'}
              </button>
            </form>
          )}
          <div className="mt-8 text-center text-sm text-zinc-500 border-t border-zinc-800/50 pt-6">
            Already registered? <button onClick={() => setPage('login')} className="text-white hover:text-cyan-400 font-bold transition-colors">Sign In securely</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
